import React, { Component } from 'react';
import P5Cell from '../P5Cell/P5Cell';
import './ProjectThumbnail.css';

const ThreeCell = React.lazy(() => import('../ThreeCell/ThreeCell'));

/**
 * Project card thumbnail, rendered from the project's own art script.
 *
 * The cards used to be <img> tags pointing at projectIdToImageURLBase, which is
 * a hostname that can (and did) stop resolving — leaving every card blank. The
 * platform already renders every edition in the grid by executing the on-chain
 * script, so a thumbnail can be produced the same way and never depends on an
 * image host at all.
 *
 * The seed is the project's own scriptTransactionHash rather than a minted
 * edition's hash. That keeps it to zero extra network calls (the value is
 * already on the project record, and is present in the archive cache), makes
 * the thumbnail deterministic, and works identically with or without a wallet.
 * It renders genuine output of that project's script; it is deliberately not
 * labelled as any particular edition, because it is not one.
 *
 * Falls back to the image URL when the script can't be loaded, so nothing is
 * lost for projects whose script is unavailable.
 */
class ProjectThumbnail extends Component {
  constructor(props) {
    super(props);
    this.mounted = false;
    this.state = { script: null, settled: false };
  }

  async componentDidMount() {
    this.mounted = true;
    const { project, getProjectScript } = this.props;

    if (!project || !project.scriptTransactionHash || typeof getProjectScript !== 'function') {
      if (this.mounted) this.setState({ settled: true });
      return;
    }

    try {
      const result = await getProjectScript(project.scriptTransactionHash);
      if (this.mounted) this.setState({ script: (result && result.script) || null, settled: true });
    } catch (error) {
      if (this.mounted) this.setState({ settled: true });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  render() {
    const { project, fallbackSrc, square } = this.props;
    const { script, settled } = this.state;

    if (script && project) {
      const scriptType = project.scriptType;
      const token = { tokenId: '1', transactionHash: project.scriptTransactionHash };
      const common = {
        id: `project-thumb-${project.scriptTransactionHash}`,
        square: square || '5',
        indexValue: '0',
        token,
        script,
        selected: false,
      };

      if (scriptType === 'p5js') {
        return <div className="project-thumbnail"><P5Cell {...common} /></div>;
      }
      if (['threejs', 'vanillajs', 'glsl'].includes(scriptType)) {
        return (
          <React.Suspense fallback={<div />}>
            <div className="project-thumbnail"><ThreeCell {...common} /></div>
          </React.Suspense>
        );
      }
    }

    // No script yet, or an unknown script type — keep the old image behaviour.
    if (settled && fallbackSrc) {
      return (
        <img
          src={fallbackSrc}
          alt=""
          onError={(e) => { e.target.style.display = 'none'; }}
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
        />
      );
    }

    return null;
  }
}

export default ProjectThumbnail;
